import InfiniteHitsWidget from "components/search/infinite_hits_widget"
import { loadTemplate } from "utils/load_template"
import WidgetsConfiguration from "cle/widgets_configuration"
import { waitForQuimbeeDataKey } from "controllers/quimbee_data_controller"
import Quimbee from "quimbee"

class CourseSearch {
  hitTemplate
  noResultsTemplate
  coursesUrl
  showMoreButtonLabel = loadTemplate("load_more_button")
  onMobile = (window.innerWidth <= 767)

  constructor(options) {
    this.coursesUrl = options.coursesUrl
    this.extraWidgets = options.extraWidgets
    this.facets = options.facets
    this.coursesContainerId = options.coursesContainerId
    this.loadingIndicator = options.loadingIndicator
    this.loadingDisableWith = options.loadingDisableWith
    this.loadMoreButton = document.querySelector(".btn-load-more")
    this.hitsPerPage = options.hitsPerPage
    this.statsContainer = options.statsContainer
    this.jurisdictionName = options.jurisdictionName
    this.practiceAreaName = options.practiceAreaName
    this.presentersMap = options.presentersMap
    this.preselectedPracticeAreaFilters = options.preselectedPracticeAreaFilters
    this.preselectedJurisdictionsFilters = options.preselectedJurisdictionsFilters
    this.preselectedCreditTypeFilters = options.preselectedCreditTypeFilters
    this.preselectedRatingFilters = options.preselectedRatingFilters
    this.preselectedPresenterFilters = options.preselectedPresenterFilters
    this.preselectedCourseDifficultyFilters = options.preselectedCourseDifficultyFilters
    this.preselectedCourseTypeFilters = options.preselectedCourseTypeFilters
    this.showCounters = options.showCounters
  }

  getHitTemplate() {
    const _this = this

    return function(data) {
      _this.hitTemplate = _this.hitTemplate || loadTemplate("hit_template")
      return _this.hitTemplate
    }
  }

  getEmptyTemplate() {
    const _this = this

    return function(){
      _this.noResultsTemplate = _this.noResultsTemplate || loadTemplate("no-results-template")
      return _this.noResultsTemplate
    }
  }

  getStatsTemplate() {
    const _this = this

    return function(data) {
      _this.statsTemplate = _this.statsTemplate || loadTemplate("cle-stats-template")
      return _this.statsTemplate
    }
  }

  refinementSettings() {
    const refinements = []

    const widgetsConfiguration = new WidgetsConfiguration(
      refinements,
      this.facets,
      this.showCounters,
      this.onMobile
    )

    widgetsConfiguration.addJurisdictionsWidget(this.jurisdictionName)
    widgetsConfiguration.addCreditTypesWidget(this.jurisdictionName)
    widgetsConfiguration.addPracticeAreasWidget()
    widgetsConfiguration.addPresentersWidget()
    widgetsConfiguration.addCourseDifficultyWidget()
    widgetsConfiguration.addCourseTypeWidget()
    widgetsConfiguration.addRatingWidget()
    widgetsConfiguration.addFilterChips()
    widgetsConfiguration.addClearRefinementsWidget()

    return refinements
  }

  filtersSettings() {
    const filters = []

    if (this.jurisdictionName) {
      filters.push(`jurisdictions:"${this.jurisdictionName}"`)
    }

    if (this.practiceAreaName) {
      filters.push(`practice_areas:"${this.practiceAreaName}"`)
    }

    return filters.join(",")
  }

  disjuntiveRefinementsSettings() {
    const refinements = {}

    // Do not preload user's jurisdictions if it's already filtering by jurisdiction. E.g. /cle/jurisdictions/connecticut#all-items
    if (!this.jurisdictionName && this.preselectedJurisdictionsFilters.length > 0) {
      refinements.jurisdictions = this.preselectedJurisdictionsFilters
    }

    if (this.preselectedPracticeAreaFilters.length > 0) {
      refinements.practice_areas = this.preselectedPracticeAreaFilters
    }

    if (this.preselectedCreditTypeFilters.length > 0) {
      refinements.credit_types = this.preselectedCreditTypeFilters
    }

    if (this.preselectedRatingFilters.length > 0) {
      refinements.rounded_rating = this.preselectedRatingFilters
    }

    if (this.preselectedPresenterFilters.length > 0) {
      refinements.presenters = this.preselectedPresenterFilters
    }

    if (this.preselectedCourseDifficultyFilters.length > 0) {
      refinements.course_difficulty = this.preselectedCourseDifficultyFilters
    }

    if (this.preselectedCourseTypeFilters.length > 0) {
      refinements.course_type = this.preselectedCourseTypeFilters
    }

    return refinements
  }

  customRouter() {
    const _this = this

    return {
      stateMapping: {
        stateToRoute(uiState) {
          return {
            q: uiState.query, sortBy: uiState.sortBy, jurisdictions: uiState.refinementList && uiState.refinementList.jurisdictions, credit_types: uiState.refinementList && uiState.refinementList.credit_types,
            course_difficulty: uiState.refinementList && uiState.refinementList.course_difficulty, course_type: uiState.refinementList && uiState.refinementList.course_type, jurisdiction_credit_types: uiState.hierarchicalMenu && uiState.hierarchicalMenu["credit_types_per_jurisdiction.lvl0"],
            practice_areas_and_hierarchical_topics: uiState.refinementList && uiState.refinementList.practice_areas_and_hierarchical_topics,
            practice_areas: uiState.refinementList && uiState.refinementList.practice_areas, rating: uiState.ratingMenu && uiState.ratingMenu.rounded_rating,
            presenters: uiState.refinementList && uiState.refinementList.presenters && uiState.refinementList.presenters.map((pre) => { return pre.split("||")[1] })
          }
        },
        routeToState(routeState) {
          return {
            query: routeState.q, hierarchicalMenu: { "credit_types_per_jurisdiction.lvl0": routeState.jurisdiction_credit_types },
            ratingMenu: { rounded_rating: routeState.rating },
            refinementList: {
              jurisdictions: routeState.jurisdictions, credit_types: routeState.credit_types, course_difficulty: routeState.course_difficulty, course_type: routeState.course_type,
              practice_areas_and_hierarchical_topics: routeState.practice_areas_and_hierarchical_topics, practice_areas: routeState.practice_areas, rounded_rating: routeState.rating,
              presenters: routeState.presenters ? routeState.presenters.map((pres) => { return _this.presentersMap[pres] }) : []
            }, sortBy: routeState.sortBy
          }
        }
      }
    }
  }

  afterLoadCallback() {
    // Hook up hx-* attributes to htmx listeners
    if (typeof htmx !== "undefined") { htmx.process(document.body) }
  }

  async init() {
    await waitForQuimbeeDataKey("algolia")

    const configuration = {
      algoliaSettings: Quimbee.data.algolia.CLESearch,
      hitsPerPage: this.hitsPerPage || 6,
      hitTemplate: this.getHitTemplate(), emptyTemplate: this.getEmptyTemplate(),
      refinementSettings: this.refinementSettings(), filters: this.filtersSettings(),
      disjunctiveFacetsRefinements: this.disjuntiveRefinementsSettings(), statsContainer: this.statsContainer,
      statsTemplate: this.getStatsTemplate(), facetingAfterDistinct: false,
      customRouter: this.customRouter(),
      showMore: true, showMoreText: this.showMoreButtonLabel,
      hitsRenderingWidget: new InfiniteHitsWidget({
        loadingIndicator: this.loadingIndicator,
        loadingDisableWith: this.loadingDisableWith,
        itemsContainerId: this.coursesContainerId,
        itemsEndpointUrl: this.coursesUrl,
        afterLoadCallback: this.afterLoadCallback
      }),
      extraWidgets: this.extraWidgets,
      cssClasses: {
        item: "hidden",
        loadMore: "btn btn-md btn-outlined btn-load-more u-text-bolder p-cle-courses-index_more-courses_button"
      }
    }

    const courseSearch = new Quimbee.instantSearch(configuration)

    courseSearch.checkAndSetup()
  }
}

export default CourseSearch
